/**
 * @file index.tsx
 *
 * @fileoverview Renders dates type field on
 * an entity as a date range popup input.
 */
import { EntityField } from '~/jobiqo/models/entity';
import React, { useContext, useEffect } from 'react';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';
import { deriveDateFormat } from '~/jobiqo/utils/dates';
import { AppContextInterface } from '~/jobiqo/models/app';
import { AppContext } from '~/jobiqo/context/app';
import { extractInitialDate } from '~/jobiqo/components/Form/Widgets/Date/utils';
import { Controller, useFormContext } from 'react-hook-form';
import { RangeDatePopup } from '~/vcl/atoms/01-forms/06-date/range';
import { SingleDatePopup } from '~/vcl/atoms/01-forms/06-date/single';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Checkbox } from '~/vcl/atoms/01-forms/02-checkbox';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { EntityFieldRules } from '~/jobiqo/models/config';
import { useFieldRules } from '../../../hooks/useFieldRules';

// Props used by this component.
interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * The field definition.
   */
  entityField: EntityField;
  /**
   * The field rules to check if the field is required, hidden or disabled.
   */
  fieldRules?: EntityFieldRules;
}

const DateRangeWidget = ({
  entityField: { removeDay, label, id, description, required, allowUntilToday },
  fieldRules = {},
}: Props) => {
  const intl = useIntl();
  const { control, getValues, setValue } = useFormContext();
  const values = getValues();
  const { hidden, required: ruleRequired } = useFieldRules(
    id,
    values,
    fieldRules,
  );

  const { locale }: AppContextInterface = useContext(AppContext);
  const dateFormat = deriveDateFormat(intl, removeDay);

  // When field is hidden reset field state. this needs to be done inside a hook
  // otherwise it will not work.
  useEffect(() => {
    if (hidden && !!values[id]) {
      setValue(id, null);
    }
  }, [hidden, values, id]);

  // Set the default value for the form widget when they're empty.
  useEffect(() => {
    if (values[id] === '') {
      setValue(id, {
        startDate: null,
        endDate: null,
      });
    }
  }, [values]);

  if (hidden) {
    return null;
  }

  return (
    <Controller
      name={id}
      control={control}
      render={({ field: { value, name }, fieldState: { error } }) => (
        <>
          {!value?.untilNow ? (
            <RangeDatePopup
              label={label}
              description={description}
              initialStartDate={extractInitialDate(value?.startDate)}
              initialEndDate={extractInitialDate(value?.endDate)}
              onDatesChange={(dates) => {
                // Format to what drupal wants.
                setValue(
                  name,
                  {
                    ...value,
                    startDate: dates.startDate
                      ? format(dates.startDate, 'yyyy-MM-dd')
                      : null,
                    ...(dates.endDate && {
                      endDate: dates.endDate
                        ? format(dates.endDate, 'yyyy-MM-dd')
                        : null,
                    }),
                  },
                  { shouldValidate: true },
                );
              }}
              showMonthYearPicker={removeDay}
              startDatePlaceholderText={
                dateFormat ? dateFormat.toLowerCase() : ''
              }
              endDatePlaceholderText={
                dateFormat ? dateFormat.toLowerCase() : ''
              }
              displayFormat={dateFormat}
              required={required || ruleRequired}
              error={!!error}
              locale={locale}
            />
          ) : (
            <SingleDatePopup
              id={name}
              label={label}
              description={description}
              required={required}
              placeholder={dateFormat.toLowerCase()}
              showMonthYearPicker={removeDay}
              initialDate={extractInitialDate(value?.startDate)}
              onDateChange={(date) => {
                // Format to what drupal wants.
                setValue(
                  name,
                  {
                    ...value,
                    startDate: format(date, 'yyyy-MM-dd'),
                  },
                  { shouldValidate: true },
                );
              }}
              displayFormat={dateFormat}
              error={!!error}
              locale={locale}
            />
          )}
          {allowUntilToday && (
            <>
              <Spacing />
              <Checkbox
                id={`${name}_until_today`}
                label={intl.formatMessage({
                  id: 'date_until_today',
                  defaultMessage: 'Until today',
                })}
                checked={value.untilNow}
                onChange={() => {
                  setValue(
                    name,
                    {
                      ...value,
                      untilNow: !value?.untilNow,
                    },
                    { shouldValidate: true },
                  );
                }}
              />
            </>
          )}
          {!!error && <InlineError message={error.message} />}
        </>
      )}
    />
  );
};

export default DateRangeWidget;
